import React, { useEffect, useState } from 'react';
import { 
  Grid, 
  Card, 
  Typography, 
  Box, 
  Chip, 
  Divider,
  Button,
  Stack,
  LinearProgress,
  Tooltip,
  IconButton,
  Avatar,
  useTheme,
  alpha
} from '@mui/material';
import { 
  CheckCircle as CheckIcon, 
  Error as ErrorIcon,
  Storage as StorageIcon,
  Memory as MemoryIcon,
  Speed as SpeedIcon,
  CloudQueue as CloudIcon,
  Flare as FlareIcon,
  MoreVert as MoreIcon,
  ArrowForward as ArrowIcon,
  Dns as ServerIcon,
  Inventory2 as LogsIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format, fromUnixTime } from 'date-fns';

/**
 * ESDL Status Grid Component
 * Displays a grid of host monitoring cards with system metrics
 * 
 * @param {Object} props
 * @param {Array} props.hosts - Array of host data objects with monitoring information
 * @param {boolean} props.loading - Whether the component is in loading state
 * @param {function} props.onRefresh - Function to call when refresh is requested
 */
const EsdlStatusGrid = ({ hosts = [], loading = false, onRefresh }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          Loading host status data...
        </Typography>
      </Box>
    );
  }
  
  if (!hosts || hosts.length === 0) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        flexDirection="column"
        sx={{ 
          backgroundColor: alpha(theme.palette.background.paper, 0.5),
          borderRadius: 3,
          p: 4 
        }}
      >
        <CloudIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2, opacity: 0.6 }} />
        <Typography color="text.secondary">No host data available</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
          Try adjusting your filters or check back later
        </Typography>
        {onRefresh && (
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            size="small"
          >
            Refresh Data
          </Button>
        )}
      </Box>
    );
  }
  
  const getCpuUsageColor = (load) => {
    if (!load && load !== 0) return 'primary';
    if (load > 80) return 'error';
    if (load > 60) return 'warning';
    return 'success';
  };

  const getMemoryUsageColor = (percent) => {
    if (!percent && percent !== 0) return 'primary';
    if (percent > 85) return 'error';
    if (percent > 70) return 'warning';
    return 'success';
  };

  const getDiskUsageColor = (percent) => {
    if (!percent && percent !== 0) return 'primary';
    if (percent > 90) return 'error';
    if (percent > 75) return 'warning';
    return 'success';
  };

  const getStatusBadge = (count, type) => {
    if (!count) return null;
    
    let color = 'success';
    let icon = <CheckIcon fontSize="small" />;
    
    if (type === 'error' && count > 0) {
      color = 'error';
      icon = <ErrorIcon fontSize="small" />;
    } else if (type === 'warning' && count > 0) {
      color = 'warning';
      icon = <FlareIcon fontSize="small" />;
    }
    
    return (
      <Chip 
        icon={icon}
        label={count}
        size="small"
        color={color}
        sx={{ 
          fontWeight: 'bold',
          '.MuiChip-icon': { 
            fontSize: '0.875rem',
            marginLeft: '4px',
            marginRight: '-4px'
          }
        }}
      />
    );
  };
  
  const viewHostDetails = (hostname) => {
    navigate(`/esdl-monitoring/host/${hostname}`);
  };

  return (
    <Grid container spacing={3}>
      {hosts.map((host, index) => (
        <Grid item xs={12} md={6} lg={4} key={`${host.hostname}-${index}`}>
          <Card 
            elevation={0}
            sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
              backdropFilter: 'blur(20px)',
              boxShadow: `0 10px 15px -3px ${alpha(theme.palette.background.default, 0.1)}, 0 4px 6px -2px ${alpha(theme.palette.background.default, 0.05)}`,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 20px 25px -5px ${alpha(theme.palette.background.default, 0.1)}, 0 10px 10px -5px ${alpha(theme.palette.background.default, 0.04)}`,
              }
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              p: 2,
              pb: 1
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar 
                  sx={{ 
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    fontWeight: 'bold',
                    width: 40,
                    height: 40
                  }}
                >
                  {host.hostname ? host.hostname.charAt(0).toUpperCase() : 'S'}
                </Avatar>
                <Box sx={{ ml: 1.5 }}>
                  <Typography variant="h6" component="div" fontWeight="bold">
                    {host.hostname}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip 
                      label={host.region} 
                      size="small" 
                      color="primary"
                      sx={{ height: 22, fontSize: '0.75rem' }}
                    />
                    {host.error_count > 0 && (
                      <Chip 
                        label={`${host.error_count} errors`}
                        size="small"
                        color="error"
                        icon={<ErrorIcon style={{ fontSize: '0.875rem' }} />}
                        sx={{ 
                          ml: 1, 
                          height: 22, 
                          fontSize: '0.75rem',
                          '.MuiChip-icon': {
                            marginRight: '-2px',
                            marginLeft: '2px'
                          }
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
              <IconButton size="small">
                <MoreIcon fontSize="small" />
              </IconButton>
            </Box>
            
            <Box sx={{ pt: 0, pb: 1, px: 2, flexGrow: 1 }}>
              <Typography variant="caption" display="block" color="text.secondary" gutterBottom>
                Last updated: {host.latest_timestamp ? format(fromUnixTime(host.latest_timestamp), 'MMM dd, yyyy HH:mm:ss') : 'Unknown'}
              </Typography>
              
              <Divider sx={{ my: 1.5 }} />
              
              <Stack spacing={2}>
                {/* CPU Usage */}
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Box display="flex" alignItems="center">
                      <SpeedIcon sx={{ color: getCpuUsageColor(host.system?.cpu?.load_1min), fontSize: 18, mr: 0.75 }} />
                      <Typography variant="body2" fontWeight="medium">CPU Load</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="bold" 
                      color={getCpuUsageColor(host.system?.cpu?.load_1min)}
                    >
                      {host.system?.cpu?.load_1min?.toFixed(2) || 'N/A'}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min(host.system?.cpu?.load_1min || 0, 100)} 
                    color={getCpuUsageColor(host.system?.cpu?.load_1min)}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
                
                {/* Memory & Disk Usage */}
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
                        <MemoryIcon sx={{ mr: 0.75, fontSize: 18, color: getMemoryUsageColor(host.system?.memory?.percent) }} />
                        Memory
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={host.system?.memory?.percent || 0} 
                          color={getMemoryUsageColor(host.system?.memory?.percent)}
                          sx={{ height: 6, borderRadius: 3, flexGrow: 1, mr: 1 }}
                        />
                        <Typography variant="caption" fontWeight="bold" color={getMemoryUsageColor(host.system?.memory?.percent)}>
                          {host.system?.memory?.percent?.toFixed(0) || 'N/A'}%
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
                        <StorageIcon sx={{ mr: 0.75, fontSize: 18, color: getDiskUsageColor(host.system?.disk?.percent) }} />
                        Disk
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={host.system?.disk?.percent || 0} 
                          color={getDiskUsageColor(host.system?.disk?.percent)}
                          sx={{ height: 6, borderRadius: 3, flexGrow: 1, mr: 1 }}
                        />
                        <Typography variant="caption" fontWeight="bold" color={getDiskUsageColor(host.system?.disk?.percent)}>
                          {host.system?.disk?.percent?.toFixed(0) || 'N/A'}%
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
                
                {/* Event Processing */}
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <Typography variant="body2" fontWeight="medium">Event Processing</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {getStatusBadge(host.events?.processed || 0, 'success')}
                    {getStatusBadge(host.events?.dropped || 0, 'error')}
                  </Box>
                </Box>
                
                {/* Service Status */}
                {host.services && Object.keys(host.services).length > 0 && (
                  <Box>
                    <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>Services:</Typography>
                    <Box display="flex" gap={0.75} flexWrap="wrap">
                      {Object.entries(host.services).map(([service, status]) => {
                        const statusColor = status === 'active' ? 'success' : 
                                         status === 'warning' ? 'warning' : 'error';
                        const StatusIcon = status === 'active' ? CheckIcon : 
                                          status === 'warning' ? FlareIcon : ErrorIcon;
                        
                        return (
                          <Chip 
                            key={service}
                            label={service}
                            size="small"
                            icon={<StatusIcon fontSize="small" />}
                            color={statusColor}
                            variant="outlined"
                            sx={{ 
                              fontSize: '0.75rem',
                              borderRadius: 1.5, 
                              '.MuiChip-icon': { fontSize: '0.85rem' }
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Stack>
            </Box>
            
            <Box p={2} pt={1}>
              <Button 
                variant="outlined" 
                fullWidth
                size="small"
                endIcon={<ArrowIcon />}
                onClick={() => viewHostDetails(host.hostname)}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none', 
                  fontWeight: 'medium'
                }}
              >
                View Details
              </Button>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default EsdlStatusGrid; 